import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import CheckmarkSmall from '@/assets/icons/checkmark-small';
import WalletCashIcon from '@/assets/icons/wallet-cash';
import { HowItWorksSteps, getPromotionStepContents } from '@/components/HowItWorksSteps';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { AppColors } from '@/components/lib/theme';
import { GameResponsibly } from '@/feature/invite-friends/components/GameResponsibly';
import { TermsAndConditions } from '@/feature/invite-friends/components/TermsAndConditions';
import { DetailsSection } from '@/feature/promohub/components/DetailsSection';
import { PromoProgressDetailsSkeleton } from '@/feature/promohub/components/PromoProgressDetailsSkeleton';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { designSystem } from '@/styles/styles';
import {
    PromotionDocumentDataBodyNumberedSlice,
    PromotionDocumentDataBodyRichTextSlice,
} from '@/types/prismic.generated';
import { isIos } from '@/utils/constants-platform-specific';
import { toSocialCurrency } from '@/utils/numeric/currency';
import { findSlice } from '@/utils/parse-prismic-content';
import { ResponsiveValue } from '@shopify/restyle';

import { usePromoConfig, usePromoProgress } from '../hooks/use-promo-details';
import { ProgressStatus, ProgressStep, PromoProgress, Promotion } from '../types';

const PROGRESS_QUAL_CRITERIA = ['PLAY', 'LOST_ENTRY', 'WIN_ENTRY'];
const PLACE_ENTRIES_STEP = 'place_entries';

export const PromoPageContent = ({ promotion }: { promotion?: Promotion }) => {
    const { t } = useTranslation('promohub');
    const { termsAndConditionsUrl } = usePromoConfig(promotion);
    const howItWorksSteps = findSlice<PromotionDocumentDataBodyNumberedSlice>('numbered', promotion?.data.body)?.items;
    const steps = getPromotionStepContents(howItWorksSteps);
    const details = findSlice<PromotionDocumentDataBodyRichTextSlice>('rich_text', promotion?.data.body)?.items[0]
        .richtexteditor;

    const shouldFetchProgress = PROGRESS_QUAL_CRITERIA.includes(promotion?.bonusEngineDetails?.qualCriteria ?? '');
    const { data: promoProgress, isLoading } = usePromoProgress(
        shouldFetchProgress ? promotion?.bonusEngineDetails?.id ?? '' : ''
    );

    return (
        <Box paddingHorizontal={'s16'} backgroundColor={ds.colorToken.background.neutral.base} flex={1}>
            {isLoading ? (
                <PromoProgressDetailsSkeleton />
            ) : promoProgress ? (
                <PromoProgressDetails promoProgress={promoProgress} />
            ) : (
                <Box paddingTop={'s32'}>
                    {steps && steps.length > 0 ? <HowItWorksSteps steps={steps} title={t('how_it_works')} /> : null}
                </Box>
            )}
            {details && details.length > 0 ? <DetailsSection details={details} /> : null}
            {termsAndConditionsUrl ? <TermsAndConditions link={termsAndConditionsUrl} /> : null}
            <GameResponsibly style={styles.gameResponsibly} />
        </Box>
    );
};

const PromoProgressDetails = ({ promoProgress }: { promoProgress: PromoProgress }) => {
    const { t } = useTranslation('promohub');

    return (
        <Box paddingTop={'s32'}>
            <Text variant={'headlineMedium'} paddingBottom={'s20'} color={ds.colorToken.text.primary}>
                {promoProgress.hasOptedIn ? t('your_progress') : t('how_it_works')}
            </Text>
            <Box paddingBottom={'s36'}>
                {promoProgress.steps.map((step, index) => (
                    <PromoProgressStep promoProgress={promoProgress} step={step} index={index} key={step.stepId} />
                ))}
            </Box>
        </Box>
    );
};

const PromoProgressStep = ({
    promoProgress,
    step,
    index,
}: {
    promoProgress: PromoProgress;
    step: ProgressStep;
    index: number;
}) => {
    const { hasOptedIn } = promoProgress;

    const isStepActive = step.status === ProgressStatus.ACTIVE;
    const isOptedInStepPending = step.status === ProgressStatus.PENDING && hasOptedIn;
    const shouldShowBorder = isStepActive || isOptedInStepPending;
    let borderColor: ResponsiveValue<AppColors, {}> = 'transparent';
    if (isStepActive) {
        borderColor = ds.colorToken.background.neutral.inverse;
    } else if (isOptedInStepPending) {
        borderColor = ds.colorToken.border.strong;
    }
    const placeEntriesStepStarted = promoProgress.currentAmount > 0;
    const shouldShowProgressBar = step.stepId === PLACE_ENTRIES_STEP && placeEntriesStepStarted;
    const isLastStep = index === promoProgress.steps.length - 1;
    const hasSocialWallet = useHasSocialWallet();
    return (
        <Box key={`${step.stepId}-${index}`}>
            <Row alignItems={'flex-start'}>
                <Box alignItems={'center'} marginRight={'s16'}>
                    <Box
                        backgroundColor={
                            shouldShowBorder
                                ? ds.colorToken.background.neutral.subtler
                                : ds.colorToken.background.neutral.inverse
                        }
                        width={24}
                        height={24}
                        borderRadius={'r20'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        borderWidth={shouldShowBorder ? 2 : 0}
                        borderColor={borderColor}
                    >
                        <StepIcon step={step} index={index} hasOptedIn={hasOptedIn} />
                    </Box>
                    {hasOptedIn && !isLastStep ? (
                        <Box
                            flex={1}
                            width={2}
                            backgroundColor={
                                isStepActive
                                    ? ds.colorToken.background.neutral.subtle
                                    : ds.colorToken.background.neutral.inverse
                            }
                        />
                    ) : null}
                </Box>
                <Box flex={1}>
                    {shouldShowProgressBar ? (
                        <Row paddingBottom={'s8'} alignItems={'center'} gap={'s4'}>
                            {hasSocialWallet ? (
                                <>
                                    <WalletCashIcon size={16} />
                                    <Text flexShrink={1} variant={'bodyMedium'} color={ds.colorToken.text.primary}>
                                        {`${toSocialCurrency(promoProgress.currentAmount)} / ${toSocialCurrency(
                                            promoProgress.targetAmount
                                        )} played`}
                                    </Text>
                                </>
                            ) : (
                                <Text flexShrink={1} variant={'bodyMedium'} color={ds.colorToken.text.primary}>
                                    {`$${promoProgress.currentAmount} / $${promoProgress.targetAmount} played `}
                                </Text>
                            )}
                        </Row>
                    ) : (
                        <Text
                            flexShrink={1}
                            variant={'bodyMedium'}
                            color={isOptedInStepPending ? 'neutral300' : ds.colorToken.text.primary}
                            paddingBottom={'s16'}
                        >
                            {step.title}
                        </Text>
                    )}
                    {shouldShowProgressBar ? <ProgressBar promoProgress={promoProgress} /> : null}
                </Box>
            </Row>
        </Box>
    );
};

const ProgressBar = ({ promoProgress }: { promoProgress: PromoProgress }) => {
    return (
        <Box paddingBottom={'s16'}>
            <Box
                height={4}
                backgroundColor={ds.colorToken.background.neutral.subtle}
                borderRadius={'r10'}
                marginRight={'s8'}
                position={'relative'}
                overflow={'hidden'}
            >
                <Box
                    height={4}
                    backgroundColor={ds.colorToken.background.neutral.inverse}
                    borderRadius={'r10'}
                    position={'absolute'}
                    top={0}
                    left={0}
                    width={`${Math.min((promoProgress.currentAmount / promoProgress.targetAmount) * 100, 100)}%`}
                />
            </Box>
        </Box>
    );
};

const StepIcon = ({ step, index, hasOptedIn }: { step: ProgressStep; index: number; hasOptedIn: boolean }) => {
    switch (step.status) {
        case ProgressStatus.PENDING:
            return (
                <Text
                    variant={'labelMedium'}
                    color={hasOptedIn ? ds.colorToken.text.secondary : 'neutral900'}
                    style={styles.stepNumber}
                >
                    {index + 1}
                </Text>
            );
        case ProgressStatus.ACTIVE:
            return (
                <Box
                    width={6}
                    height={6}
                    borderRadius={'r6'}
                    backgroundColor={ds.colorToken.background.neutral.inverse}
                />
            );
        case ProgressStatus.COMPLETED:
            return <CheckmarkSmall width={16} height={16} color={designSystem.colors.neutralBlack} />;
        default:
            return null;
    }
};

const styles = StyleSheet.create({
    title: {
        fontSize: 40,
        lineHeight: 40,
        // on android, the custom font breaks if we add a fontWeight
        fontWeight: isIos ? '800' : undefined,
        fontFamily: 'gamay-extrabold',
        textAlign: 'center',
        letterSpacing: -1.6,
        textTransform: 'uppercase',
        paddingHorizontal: 8,
    },
    image: {
        width: 160,
        height: 160,
    },
    imageContainer: {
        paddingTop: 21,
    },
    topGlowContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
    },
    bottomGlowContainer: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        zIndex: -1,
    },
    supportingText: {
        textAlign: 'center',
        marginTop: 5,
        letterSpacing: -0.43,
        lineHeight: 24,
    },
    optInButton: {
        marginBottom: 20,
        borderWidth: 2,
        paddingVertical: 12,
    },
    optInButtonDisabled: {
        backgroundColor: 'transparent',
    },
    expiresIn: {
        fontWeight: '600',
        textTransform: 'uppercase',
    },
    clockIcon: {
        marginRight: 4,
    },
    stepNumber: {
        fontWeight: '700',
    },
    termsAndConditions: {
        textDecorationLine: 'underline',
        textDecorationStyle: 'solid',
    },
    gameResponsibly: {
        paddingTop: 56,
        paddingBottom: 46,
    },
    stepSeparator: {
        position: 'absolute',
        left: 11,
    },
});
